<template>
  <ModalQmulus
    visible
    :title="conflict.dialog.title ?? translations.title"
    :closable="!conflict.dialog.isSubmitting"
    :close-on-escape="!conflict.dialog.isSubmitting"
    width="72rem"
    max-width="95vw"
    data-qa="modal-merge-conflict"
    @update:visible="onVisibleUpdate"
  >
    <div
      class="flex-column-section"
      :style="SEMANTIC_COLOR_VARIABLES"
    >
      <p
        v-if="hasChanges"
        class="modal-merge-conflict__description"
      >
        {{ conflict.dialog.conflicts.length > 0 ? translations.descriptionConflicts : translations.descriptionServerChanges }}
      </p>
      <MessageBox
        v-for="warning in conflict.dialog.warnings"
        :key="warning"
        severity="warning"
        :summary="translations.notSaved"
        :details="warning"
        data-qa="modal-merge-conflict__warning"
      />
      <div
        v-if="conflict.dialog.conflicts.length > 0"
        class="modal-merge-conflict__conflicts"
        data-qa="modal-merge-conflict__conflicts"
      >
        <div class="modal-merge-conflict__grid-row modal-merge-conflict__header">
          <span>{{ translations.field }}</span>
          <div class="modal-merge-conflict__columns">
            <span
              v-for="version in THREE_WAY_VERSIONS"
              :key="version"
              class="modal-merge-conflict__column-title"
            >
              {{ translations.versions[version] }}
              <LinkWithIcon
                v-if="version !== 'original'"
                :text="translations.keepAll"
                :on-click="() => conflict.chooseForAllConflicts(version)"
                :data-qa="`modal-merge-conflict__keep-all-${version}`"
              />
            </span>
          </div>
        </div>
        <div
          v-for="row in conflict.dialog.conflicts"
          :key="row.id"
          class="modal-merge-conflict__grid-row modal-merge-conflict__row"
          data-qa="modal-merge-conflict__row"
          :data-qa-id="row.id"
        >
          <div class="modal-merge-conflict__field">
            <span>{{ row.label }}</span>
            <span
              v-if="row.position"
              class="modal-merge-conflict__secondary"
            >{{ row.position }}</span>
          </div>
          <RadioCardGroup
            :model-value="conflict.dialog.choices[row.id]"
            :options="getChoiceOptions(row)"
            :aria-label="row.label"
            :columns="THREE_WAY_VERSIONS.length"
            :disabled="conflict.dialog.isSubmitting"
            @update:model-value="conflict.setChoice(row.id, toChoice($event))"
          >
            <template
              #option="{
                option, isSelected,
              }"
            >
              <div class="modal-merge-conflict__card">
                <span
                  v-if="option.value === 'combined'"
                  class="modal-merge-conflict__card-title"
                >{{ translations.combined }}</span>
                <MergeConflictValue
                  class="modal-merge-conflict__card-value"
                  :value="getValueView(row, toChoice(option.value))"
                />
                <span
                  v-if="getCardMeta(toChoice(option.value), isSelected)"
                  class="modal-merge-conflict__secondary"
                >{{ getCardMeta(toChoice(option.value), isSelected) }}</span>
              </div>
            </template>
          </RadioCardGroup>
        </div>
      </div>
      <div
        v-if="conflict.dialog.serverChanges.length > 0"
        class="modal-merge-conflict__server-changes"
        data-qa="modal-merge-conflict__server-changes"
      >
        <div class="modal-merge-conflict__server-changes-summary">
          <i
            class="pi pi-info-circle"
            aria-hidden="true"
          />
          <span>{{ serverChangesSummary }}</span>
          <LinkWithIcon
            v-if="conflict.dialog.conflicts.length > 0"
            :text="areServerChangesShown ? translations.hide : translations.show"
            :on-click="toggleServerChanges"
            data-qa="modal-merge-conflict__toggle-server-changes"
          />
        </div>
        <ul
          v-if="areServerChangesShown"
          class="modal-merge-conflict__server-change-list"
        >
          <li
            v-for="row in conflict.dialog.serverChanges"
            :key="row.id"
            class="modal-merge-conflict__server-change"
            :class="{
              'modal-merge-conflict__server-change_reverted': isReverted(row),
            }"
            data-qa="modal-merge-conflict__server-change"
            :data-qa-id="row.id"
          >
            <span>
              {{ row.label }}<template v-if="row.position">, {{ row.position }}</template>:
            </span>
            <template v-if="row.values.original.status === 'missing'">
              <span class="modal-merge-conflict__added">
                <i
                  class="pi pi-plus modal-merge-conflict__change-icon"
                  aria-hidden="true"
                />
                {{ translations.added }}
              </span>
              <MergeConflictValue
                class="modal-merge-conflict__server-change-value"
                :value="row.values.latest"
                inline
              />
            </template>
            <template v-else-if="row.values.latest.status === 'removed'">
              <MergeConflictValue
                class="modal-merge-conflict__server-change-value"
                :value="row.values.latest"
                inline
              />
              <MergeConflictValue
                class="modal-merge-conflict__removed-value"
                :value="row.values.original"
                inline
              />
            </template>
            <template v-else>
              <MergeConflictValue
                :value="row.values.original"
                inline
              />
              <i
                class="pi pi-arrow-right modal-merge-conflict__arrow"
                aria-hidden="true"
              />
              <MergeConflictValue
                class="modal-merge-conflict__server-change-value"
                :value="row.values.latest"
                inline
              />
            </template>
            <span
              v-if="isReverted(row)"
              class="modal-merge-conflict__secondary"
            >{{ translations.reverted }}</span>
            <LinkWithIcon
              :text="isReverted(row) ? translations.restore : translations.revert"
              :on-click="() => toggleRevert(row)"
              data-qa="modal-merge-conflict__revert"
            />
          </li>
        </ul>
      </div>
    </div>
    <template #actions>
      <span
        v-if="conflict.dialog.conflicts.length > 0"
        class="modal-merge-conflict__status"
        data-qa="modal-merge-conflict__status"
      >
        <PrmTag
          v-if="unresolvedCount > 0"
          :value="resolvedText"
          :pt="AWAITING_INPUT_TAG_PASSTHROUGH"
        />
        <span
          v-else
          class="modal-merge-conflict__secondary"
        >{{ resolvedText }}</span>
      </span>
      <SemanticButtonGroup :buttons="actionButtons" />
    </template>
  </ModalQmulus>
</template>

<script setup lang="ts">
import PrmTag from '@NitrexGroup/primevue/tag';
import {
  computed,
  shallowRef,
} from 'vue';

import {
  IActiveMergeConflict,
  IMergeConflictRowView,
  IMergeConflictValueView,
  getConflictChoices,
} from '../../composable/use-merge-conflict.ts';
import {
  SemanticColors,
} from '../../constants/color.ts';
import useLibraryI18n from '../../i18n/composable.ts';
import {
  buildTagColorPassthrough,
} from '../../utils/primevue-tag.ts';
import {
  THREE_WAY_CHOICES,
  THREE_WAY_VERSIONS,
  TThreeWayChoice,
  TThreeWayVersion,
} from '../../utils/three-way-diff.ts';
import SemanticButtonGroup, {
  TSemanticButtonGroupProps,
} from '../button/semantic-button-group.vue';
import LinkWithIcon from '../link-with-icon/link-with-icon.vue';
import MessageBox from '../message-box/message-box.vue';
import ModalQmulus from '../modal-qmulus/modal-qmulus.vue';
import {
  IRadioCardOption,
} from '../radio-card-group/radio-card-group.ts';
import RadioCardGroup from '../radio-card-group/radio-card-group.vue';

import MergeConflictValue from './merge-conflict-value.vue';

const props = defineProps<{
  conflict: IActiveMergeConflict,
}>();

// A change made in the meantime is something to be aware of, not a success or a failure.
const SEMANTIC_COLOR_VARIABLES = {
  '--modal-merge-conflict-change-color': SemanticColors.informational,
};

// Conflicts without a choice wait for the user's decision.
const AWAITING_INPUT_TAG_PASSTHROUGH = buildTagColorPassthrough(SemanticColors.awaitingInput, 'white');

const {
  t,
} = useLibraryI18n('components/modal-merge-conflict/modal-merge-conflict');

const translations = computed(() => ({
  title: t('title'),
  descriptionConflicts: t('descriptionConflicts'),
  descriptionServerChanges: t('descriptionServerChanges'),
  notSaved: t('notSaved'),
  field: t('field'),
  keepAll: t('keepAll'),
  keeping: t('keeping'),
  you: t('you'),
  combined: t('combined'),
  added: t('added'),
  show: t('show'),
  hide: t('hide'),
  revert: t('revert'),
  restore: t('restore'),
  reverted: t('reverted'),
  cancelTooltip: t('cancelTooltip'),
  versions: {
    original: t('version.original'),
    latest: t('version.latest'),
    edited: t('version.edited'),
  } satisfies Record<TThreeWayVersion, string>,
}));

const hasChanges = computed<boolean>(() => props.conflict.dialog.conflicts.length > 0
  || props.conflict.dialog.serverChanges.length > 0);

const resolvedCount = computed<number>(() => props.conflict.dialog.conflicts
  .filter((row) => props.conflict.dialog.choices[row.id] !== undefined)
  .length);
const unresolvedCount = computed<number>(() => props.conflict.dialog.conflicts.length - resolvedCount.value);

const resolvedText = computed<string>(() => t('resolvedCount', {
  resolved: resolvedCount.value,
  total: props.conflict.dialog.conflicts.length,
}, props.conflict.dialog.conflicts.length));

const serverChangesSummary = computed<string>(() => {
  const summary = t('serverChanges', {
    count: props.conflict.dialog.serverChanges.length,
  });
  return props.conflict.dialog.latestAuthor
    ? `${summary} (${props.conflict.dialog.latestAuthor})`
    : summary;
});

const isServerChangesListOpen = shallowRef(false);
const areServerChangesShown = computed<boolean>(() => isServerChangesListOpen.value
  || props.conflict.dialog.conflicts.length === 0);

function toggleServerChanges() {
  isServerChangesListOpen.value = !isServerChangesListOpen.value;
}

/** The card group types its values as strings in the slot and the update event. */
function toChoice(cardValue: string): TThreeWayChoice {
  const choice = THREE_WAY_CHOICES.find((candidate) => candidate === cardValue);
  if (!choice) {
    throw new Error(`Unknown merge conflict choice ${cardValue}.`);
  }
  return choice;
}

function getChoiceOptions(row: IMergeConflictRowView): IRadioCardOption<TThreeWayChoice>[] {
  return getConflictChoices(row).map((choice) => ({
    value: choice,
    isWide: choice === 'combined',
  }));
}

function getValueView(row: IMergeConflictRowView, choice: TThreeWayChoice): IMergeConflictValueView {
  return choice === 'combined'
    ? row.combinedValue!
    : row.values[choice];
}

function getCardMeta(choice: TThreeWayChoice, isSelected: boolean): string {
  const metaParts: string[] = [];
  if (choice === 'latest' && props.conflict.dialog.latestAuthor) {
    metaParts.push(props.conflict.dialog.latestAuthor);
  } else if (choice === 'edited') {
    metaParts.push(translations.value.you);
  }
  if (isSelected) {
    metaParts.push(translations.value.keeping);
  }
  return metaParts.join(' · ');
}

function isReverted(row: IMergeConflictRowView): boolean {
  return props.conflict.dialog.choices[row.id] === 'edited';
}

function toggleRevert(row: IMergeConflictRowView) {
  props.conflict.setChoice(row.id, isReverted(row) ? 'latest' : 'edited');
}

const actionButtons = computed<TSemanticButtonGroupProps>(() => ({
  cancel: {
    disabled: props.conflict.dialog.isSubmitting,
    tooltip: translations.value.cancelTooltip,
    onClick: () => props.conflict.cancel(),
  },
  save: {
    loading: props.conflict.dialog.isSubmitting,
    disabled: unresolvedCount.value > 0,
    tooltip: unresolvedCount.value > 0
      ? t('saveDisabled', unresolvedCount.value)
      : undefined,
    // Not returned on purpose: Vue catches a promise returned by an event handler and, in
    // production builds, only logs its rejection, so a global error handler would not see it.
    onClick: () => {
      props.conflict.save();
    },
  },
}));

function onVisibleUpdate(isVisible: boolean) {
  if (!isVisible) {
    props.conflict.dismiss();
  }
}
</script>

<style lang="scss" scoped>
.modal-merge-conflict {
  &__description {
    margin: 0;
  }

  &__conflicts {
    display: flex;
    flex-direction: column;
  }

  &__grid-row {
    display: grid;
    grid-template-columns: minmax(9rem, 1fr) minmax(0, 4fr);
    gap: var(--gap);
    align-items: start;
    padding: var(--gap) 0;
  }

  &__header {
    padding-top: 0;
    font-weight: bold;
  }

  &__row {
    border-top: 1px solid var(--border-color-subtle);
  }

  &__columns {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--gap);
  }

  &__column-title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--gap-union);

    :deep(.link-with-icon) {
      font-weight: normal;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__secondary {
    font-size: 0.875rem;
    color: var(--text-secondary-color);
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: var(--gap-union);
  }

  &__card-title {
    font-weight: bold;
  }

  &__card-value {
    font-weight: 500;
  }

  &__server-changes {
    display: flex;
    flex-direction: column;
    gap: var(--gap-union);
  }

  &__server-changes-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--gap-union);
  }

  &__server-change-list {
    display: flex;
    flex-direction: column;
    gap: var(--gap-union);
    margin: 0;
    padding-left: 1.5rem;
  }

  &__server-change {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--gap-union);
  }

  &__server-change_reverted &__server-change-value {
    opacity: 0.5;
  }

  &__arrow {
    font-size: 0.75rem;
    color: var(--text-secondary-color);
  }

  &__added {
    display: inline-flex;
    align-items: center;
    gap: var(--gap-union);
  }

  &__change-icon {
    color: var(--modal-merge-conflict-change-color);
  }

  &__removed-value {
    text-decoration: line-through;
  }

  &__status {
    margin-right: auto;
    align-self: center;
  }
}
</style>
